import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { BackLink, Button, CardHeader, ErrorNotice, FactList, Field, LoadingState, PageEyebrow, Panel, Pill, SegmentedControl, SelectInput, TextArea, TextInput, formatCredits, type PillTone } from '@bcecloud/portal-ui';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { InquiryWorkroom } from '@/components/InquiryWorkroom';
import { errorMessage } from '@/lib/api';
import { BOOKING_DECISION, CANCELLATION_DECISION, decideBooking, readBookingInquiry, settleCancellation, type BookingDecision, type BookingInquiry, type CancellationDecision } from '@/lib/inquiries';

/**
 * One event request, worked as an inquiry: the facts the client gave, BCE's
 * tasks, decisions and notes, and the decision that promotes it to an event,
 * parks it with the client, holds it, or declines it.
 */

const STATUS_LABELS: Readonly<Record<string, { readonly label: string; readonly tone: PillTone }>> = {
  SUBMITTED: { label: 'Submitted', tone: 'neutral' },
  AWAITING_TEAM_APPROVAL: { label: 'Awaiting team approval', tone: 'neutral' },
  AWAITING_ORG_APPROVAL: { label: 'Awaiting client approval', tone: 'neutral' },
  UNDER_BCE_REVIEW: { label: 'Under review', tone: 'warning' },
  DATE_HELD: { label: 'Date held', tone: 'info' },
  INFORMATION_REQUIRED: { label: 'Information needed', tone: 'danger' },
  APPROVED: { label: 'Approved: now an event', tone: 'success' },
  CONFIRMED: { label: 'Confirmed', tone: 'success' },
  CANCELLED: { label: 'Declined', tone: 'neutral' },
};

/** Statuses from which BCE can still decide. */
const DECIDABLE: ReadonlySet<string> = new Set(['UNDER_BCE_REVIEW', 'DATE_HELD', 'INFORMATION_REQUIRED']);

const DECISIONS: readonly { readonly key: BookingDecision; readonly label: string; readonly title: string; readonly consequence: string; readonly tone: 'primary' | 'secondary' | 'danger' }[] = [
  { key: BOOKING_DECISION.promote, label: 'Promote to event', title: 'Promote this request to an event?', consequence: 'It leaves the inquiry stage. Its credits become firm, it appears on the Events tab, and planning starts on its workspace.', tone: 'primary' },
  { key: BOOKING_DECISION.needsInformation, label: 'Needs information', title: 'Ask the client for more?', consequence: 'The request waits with the client until they answer. Say what you need in the note.', tone: 'secondary' },
  { key: BOOKING_DECISION.hold, label: 'Hold the date', title: 'Hold the date?', consequence: 'The date is held while the work continues.', tone: 'secondary' },
  { key: BOOKING_DECISION.decline, label: 'Decline', title: 'Decline this request?', consequence: 'The request is canceled and any credits held for it are released. Say why in the note.', tone: 'danger' },
];

function longDate(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export function BookingInquiryPage(): React.ReactElement {
  const { organizationId = '', bookingId = '' } = useParams();
  const queryClient = useQueryClient();
  const booking = useQuery({ queryKey: ['org-booking', organizationId, bookingId], queryFn: ({ signal }) => readBookingInquiry(organizationId, bookingId, signal) });

  const [deciding, setDeciding] = useState<BookingDecision | null>(null);
  const [outcome, setOutcome] = useState('');
  const [clientNote, setClientNote] = useState('');
  const decide = useMutation({
    mutationFn: (decision: BookingDecision) =>
      decideBooking(organizationId, bookingId, {
        decision,
        ...(outcome ? { outcome } : {}),
        ...(clientNote.trim() ? { clientNote: clientNote.trim() } : {}),
      }),
    onSuccess: async () => {
      setDeciding(null);
      toast.success('Decision recorded. The client sees the outcome on their portal.');
      await queryClient.invalidateQueries({ queryKey: ['org-booking', organizationId, bookingId] });
      await queryClient.invalidateQueries({ queryKey: ['org-review-queue', organizationId] });
      await queryClient.invalidateQueries({ queryKey: ['org-workspaces', organizationId] });
    },
  });

  if (booking.isPending) return <LoadingState label="Loading the request…" />;
  if (booking.isError) return <ErrorNotice title="Request unavailable" message={errorMessage(booking.error)} />;
  const data = booking.data;
  const status = STATUS_LABELS[data.status] ?? { label: data.status, tone: 'neutral' as PillTone };
  const decidable = DECIDABLE.has(data.status);
  const chosen = DECISIONS.find((decision) => decision.key === deciding) ?? null;

  return (
    <section className="animate-portal-rise">
      <BackLink to={`/organizations/${organizationId}?tab=enquiries`}>All inquiries</BackLink>

      <header className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <PageEyebrow>EVENT REQUEST</PageEyebrow>
          <h1 className="text-portal-page tracking-[-1.7px]">{data.programName}</h1>
          <p className="mt-1 text-sm text-admin-muted">
            {data.reference}
            {data.submittedAt ? ` · submitted ${longDate(data.submittedAt)}` : ''}
            {data.requester ? ` · by ${data.requester.name}` : ''}
            {data.teamName ? ` · ${data.teamName}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone={status.tone}>{status.label}</Pill>
          {decidable
            ? DECISIONS.map((decision) => (
                <Button
                  key={decision.key}
                  variant={decision.tone === 'primary' ? 'primary' : 'secondary'}
                  className={decision.tone === 'danger' ? 'border-red-200 text-red-600 hover:bg-red-50' : undefined}
                  onClick={() => {
                    setOutcome(data.feasibility.outcome ?? '');
                    setClientNote(data.feasibility.clientNote ?? '');
                    setDeciding(decision.key);
                  }}
                >
                  {decision.label}
                </Button>
              ))
            : null}
        </div>
      </header>

      {data.cancellationRequest ? (
        <CancellationSettlement organizationId={organizationId} booking={data} request={data.cancellationRequest} />
      ) : null}

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Panel className="p-5">
          <CardHeader title="The request" description="As the client entered it." />
          <FactList
            columns={2}
            showEmpty
            facts={[
              { label: 'Date', value: longDate(data.preferredDate) },
              { label: 'Alternate date', value: longDate(data.alternateDate) },
              { label: 'Location', value: [data.venueName, [data.city, data.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ') || null },
              { label: 'Venue status', value: data.venueStatus },
              { label: 'Delivery', value: data.deliveryMethod },
              { label: 'Participants', value: String(data.estimatedAttendees) },
              { label: 'Credits', value: String(data.estimatedCredits) },
              { label: 'On-site contact', value: data.onSiteContact },
              { label: 'Venue contact', value: data.venueContact },
            ]}
          />
        </Panel>
        <Panel className="p-5">
          <CardHeader title="What they want from it" description="Objectives and asks, in their words." />
          <FactList
            columns={2}
            showEmpty
            facts={[
              { label: 'Objectives', value: data.objectives },
              { label: 'Notes', value: data.notes },
              { label: 'Special requirements', value: data.specialRequirements },
              { label: 'Branding requests', value: data.brandingRequests },
              { label: 'Charity interests', value: data.csrInterests },
              { label: 'Accessibility needs', value: data.accessibilityNeeds },
            ]}
          />
        </Panel>
      </div>

      {data.feasibility.outcome || data.feasibility.clientNote ? (
        <Panel className="mt-6 p-5">
          <CardHeader
            title="What the client sees"
            description={
              data.feasibility.reviewedAt
                ? `Recorded${data.feasibility.reviewedByName ? ` by ${data.feasibility.reviewedByName}` : ''} ${longDate(data.feasibility.reviewedAt) ?? ''}.`
                : 'The outcome and note beside the request on their portal.'
            }
          />
          <FactList
            columns={2}
            showEmpty
            facts={[
              { label: 'Outcome', value: data.outcomes.find((entry) => entry.value === data.feasibility.outcome)?.label ?? data.feasibility.outcome },
              { label: 'Note to the client', value: data.feasibility.clientNote },
            ]}
          />
        </Panel>
      ) : null}

      <div className="mt-6">
        <InquiryWorkroom organizationId={organizationId} subject={{ type: 'booking_request', id: bookingId }} />
      </div>

      {chosen ? (
        <Dialog open onOpenChange={(open) => !open && setDeciding(null)}>
          <DialogContent className="max-w-lg">
            <DialogTitle>{chosen.title}</DialogTitle>
            <p className="mt-2 text-portal-small text-portal-muted">{chosen.consequence}</p>
            <div className="mt-4 grid gap-4">
              <Field label="Outcome the client sees" htmlFor="decision-outcome" hint="The one line that reads on their portal beside the request.">
                <SelectInput id="decision-outcome" value={outcome} onChange={(event) => setOutcome(event.target.value)}>
                  <option value="">Choose one</option>
                  {data.outcomes.map((entry) => (
                    <option key={entry.value} value={entry.value}>
                      {entry.label}
                    </option>
                  ))}
                </SelectInput>
              </Field>
              <Field label="Note to the client" htmlFor="decision-note" hint="Optional. The only free text that leaves BCE; it appears with the outcome.">
                <TextArea id="decision-note" rows={3} value={clientNote} onChange={(event) => setClientNote(event.target.value)} />
              </Field>
            </div>
            {decide.isError ? <ErrorNotice inline title="Not recorded" message={errorMessage(decide.error)} /> : null}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeciding(null)} disabled={decide.isPending}>
                Not yet
              </Button>
              <Button
                variant={chosen.tone === 'danger' ? 'secondary' : 'primary'}
                className={chosen.tone === 'danger' ? 'border-red-200 text-red-600 hover:bg-red-50' : undefined}
                onClick={() => decide.mutate(chosen.key)}
                disabled={decide.isPending}
              >
                {decide.isPending ? 'Recording…' : chosen.label}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </section>
  );
}

const SETTLE_OPTIONS = [
  { key: CANCELLATION_DECISION.cancel, label: 'Cancel the event' },
  { key: CANCELLATION_DECISION.keep, label: 'Keep it' },
] as const;

/**
 * A client asked to cancel this confirmed event (launch polish, 2026-09-25).
 * BCE settles it: cancel and return what is recoverable (all of it unless
 * said), or keep it and tell the client why.
 */
function CancellationSettlement({
  organizationId,
  booking,
  request,
}: {
  readonly organizationId: string;
  readonly booking: BookingInquiry;
  readonly request: { readonly at: string; readonly byName: string | null; readonly reason: string };
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [decision, setDecision] = useState<CancellationDecision>(CANCELLATION_DECISION.cancel);
  const [credits, setCredits] = useState(String(booking.estimatedCredits));
  const [note, setNote] = useState('');
  const amount = Number(credits);
  // Two decimals at most: a credit is $100 and events are priced to the dollar.
  const validAmount =
    credits.trim() !== '' && Number.isFinite(amount) && amount >= 0 && amount <= booking.estimatedCredits && Math.abs(Math.round(amount * 100) - amount * 100) < 1e-6;
  const settle = useMutation({
    mutationFn: () =>
      settleCancellation(organizationId, booking.id, {
        decision,
        ...(decision === CANCELLATION_DECISION.cancel ? { creditsToReturn: amount } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: async (result) => {
      toast.success(result.cancelled ? `Canceled; ${formatCredits(result.creditsReturned)} credits returned.` : 'Kept; the client sees why.');
      await queryClient.invalidateQueries({ queryKey: ['org-booking', organizationId, booking.id] });
      await queryClient.invalidateQueries({ queryKey: ['org-workspaces', organizationId] });
    },
  });
  const canSettle =
    !settle.isPending &&
    (decision === CANCELLATION_DECISION.cancel ? validAmount : note.trim().length > 0);

  return (
    <Panel className="mt-6 border-amber-200 p-5">
      <CardHeader
        title="Cancellation requested"
        description={`${request.byName ?? 'The client'} asked on ${longDate(request.at) ?? 'an unknown date'}. The event stays booked until you settle it.`}
      />
      <p className="mb-4 max-w-[80ch] border-l-2 border-portal-line pl-3 text-portal-small whitespace-pre-line">{request.reason}</p>
      <div className="grid max-w-xl gap-4">
        <SegmentedControl label="Settle it" options={SETTLE_OPTIONS} value={decision} onChange={setDecision} />
        {decision === CANCELLATION_DECISION.cancel ? (
          <Field
            label="Credits to return"
            htmlFor="settle-credits"
            required
            hint={`Up to ${formatCredits(booking.estimatedCredits)} committed; two decimals at most. The rest is kept under the cancellation terms.`}
          >
            <TextInput id="settle-credits" type="number" min={0} step={0.01} value={credits} onChange={(event) => setCredits(event.target.value)} />
          </Field>
        ) : null}
        <Field
          label={decision === CANCELLATION_DECISION.cancel ? 'Note for the client' : 'Why the event goes ahead'}
          htmlFor="settle-note"
          required={decision === CANCELLATION_DECISION.keep}
          hint="The client reads this on their event."
        >
          <TextArea id="settle-note" rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
        {settle.isError ? <ErrorNotice inline title="Not settled" message={errorMessage(settle.error)} /> : null}
        <div>
          <Button onClick={() => settle.mutate()} disabled={!canSettle}>
            {settle.isPending ? 'Saving…' : decision === CANCELLATION_DECISION.cancel ? 'Cancel and return credits' : 'Keep the event'}
          </Button>
        </div>
      </div>
    </Panel>
  );
}
